import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, ChevronsRight, RefreshCw, BedDouble, Wand2, SlidersHorizontal, Plus, X, Trash2, Globe } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toLocalISODate } from "../utils/date";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

/**
 * Rates & Inventory — a date-across grid, split into the two views that
 * actually answer different questions:
 *
 *   Inventory — how many of each room type are left, night by night.
 *   Rates     — what each room type costs, night by night.
 *
 * Deliberately TWO tabs and not the seven a channel-manager grid shows.
 * Minimum/Maximum Nights, Stopsells, COA and COD are booking-engine RULES,
 * not display: storing them without enforcing them in the booking routes,
 * the availability calculation and the OTA sync would let a property set a
 * two-night minimum, see it saved, and keep taking one-night bookings.
 *
 * That enforcement now exists — services/stayRules.js gates the public
 * availability endpoint and the direct booking route, and
 * services/channex/ari.js sends the same rules to the OTAs — so the rules
 * are editable here, through "Set rates & rules". They are still not grid
 * columns: a rule covers a span, and reading a span off fourteen separate
 * cells is worse than reading the list of spans it actually is.
 *
 * Rates are read-only here. Every change goes through the validated
 * rate-override routes (the "Set rate" panel), which carry the overlap check
 * and the all-or-nothing guarantee — an inline cell editor would have to
 * duplicate that or skip it, and skipping it is how a live property ends up
 * with two seasons claiming one night at different prices.
 */
const DAYS = 14;

const fmtMoney = n => (n ? `₹${Number(n).toLocaleString("en-IN")}` : "—");
const dayMeta = ds => {
  const d = new Date(`${ds}T00:00:00`);
  return {
    dow: d.toLocaleDateString("en-IN", { weekday: "short" }),
    num: d.getDate(),
    mon: d.toLocaleDateString("en-IN", { month: "short" }),
    weekend: [0, 6].includes(d.getDay()),
  };
};

const TABS = ["Inventory", "Rates"];

export default function RatesPage() {
  const { authHeaders } = useAuth();
  const [tab, setTab]         = useState(() => {
    try { return localStorage.getItem("hostsync_rates_tab") || "Inventory"; } catch { return "Inventory"; }
  });
  const [from, setFrom]       = useState(() => toLocalISODate(new Date()));
  const [roomFilter, setRoomFilter] = useState("all");
  const [data, setData]       = useState(null);
  const [error, setError]     = useState("");
  const [loading, setLoading] = useState(false);
  const [bulkFor, setBulkFor] = useState(null);

  const selectTab = t => {
    setTab(t);
    try { localStorage.setItem("hostsync_rates_tab", t); } catch {}
  };

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/rates-grid?from=${from}&days=${DAYS}`, { headers: authHeaders() });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Could not load rates");
      setData(json.data);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [from]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const shift = n => {
    const d = new Date(`${from}T00:00:00`);
    d.setDate(d.getDate() + n);
    setFrom(toLocalISODate(d));
  };

  // Filtering is client-side on purpose: the grid is already loaded, so
  // changing room type is instant rather than another round trip.
  const visibleRooms = useMemo(() => {
    if (!data) return [];
    return roomFilter === "all" ? data.rooms : data.rooms.filter(r => r.roomId === roomFilter);
  }, [data, roomFilter]);

  // Footer totals must reflect the FILTER, not the whole property — showing
  // property-wide totals under a single filtered room would misreport it.
  const totals = useMemo(() => {
    if (!data) return [];
    return data.dates.map((date, i) => ({
      date,
      sold:      visibleRooms.reduce((n, r) => n + r.cells[i].sold, 0),
      available: visibleRooms.reduce((n, r) => n + r.cells[i].available, 0),
      total:     visibleRooms.reduce((n, r) => n + r.totalUnits, 0),
    }));
  }, [data, visibleRooms]);

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Rates &amp; Inventory</h1>
          <p className="text-sm text-gray-500">What's left and what it costs, night by night.</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 -mx-1 px-1 overflow-x-auto">
        {TABS.map(t => (
          <button key={t} onClick={() => selectTab(t)}
            className={`px-4 py-2.5 min-h-[44px] text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
              tab === t ? "border-amber-500 text-amber-600" : "border-transparent text-gray-500 hover:text-gray-800"
            }`}>
            {t}
          </button>
        ))}
      </div>

      {/* One place to price and close dates. Deliberately multi-room and
          multi-line: every line in one Apply is one save, which is one push
          to the channel manager rather than one per room. */}
      <BulkRulesPanel rooms={data?.rooms || []} authHeaders={authHeaders} onApplied={load} />
      <OtaPricingPanel rooms={data?.rooms || []} authHeaders={authHeaders} />

      {/* Filter + date navigation */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <select value={roomFilter} onChange={e => setRoomFilter(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white
            focus:outline-none focus:ring-2 focus:ring-amber-400 min-w-[180px]">
          <option value="all">All room types</option>
          {(data?.rooms || []).map(r => <option key={r.roomId} value={r.roomId}>{r.name}</option>)}
        </select>

        <div className="flex items-center gap-1.5">
          <button onClick={() => shift(-DAYS)} aria-label="Previous dates"
            className="tap-44 p-2 rounded-xl hover:bg-gray-100 transition-colors">
            <ChevronLeft size={18} className="text-gray-500" />
          </button>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
          <button onClick={() => shift(DAYS)} aria-label="Next dates"
            className="tap-44 p-2 rounded-xl hover:bg-gray-100 transition-colors">
            <ChevronRight size={18} className="text-gray-500" />
          </button>
          <button onClick={() => shift(DAYS * 2)} aria-label="Skip forward"
            className="tap-44 p-2 rounded-xl hover:bg-gray-100 transition-colors">
            <ChevronsRight size={18} className="text-gray-500" />
          </button>
          <button onClick={load} aria-label="Refresh"
            className="tap-44 p-2 rounded-xl hover:bg-gray-100 transition-colors">
            <RefreshCw size={16} className={`text-gray-500 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-3">{error}</p>}
      {!data && !error && <p className="text-sm text-gray-400">Loading…</p>}

      {data && visibleRooms.length === 0 && (
        <p className="text-sm text-gray-400 bg-white border border-gray-100 rounded-2xl px-5 py-8 text-center">
          {data.rooms.length === 0 ? "No room types yet — add one under Rooms first." : "No room type matches that filter."}
        </p>
      )}

      {data && visibleRooms.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
          <table className="text-sm border-collapse min-w-[900px] w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="sticky left-0 z-10 bg-white text-left text-[10.5px] font-semibold text-gray-400
                  uppercase tracking-wider px-4 py-3 min-w-[210px]">Room Type</th>
                {data.dates.map(ds => {
                  const { dow, num, mon, weekend } = dayMeta(ds);
                  return (
                    <th key={ds} className={`px-2 py-2 text-center min-w-[78px] border-l border-gray-50
                      ${weekend ? "bg-amber-50/60" : ""}`}>
                      <div className="text-[10px] font-semibold text-gray-400 uppercase">{dow}</div>
                      <div className="text-sm font-bold text-gray-900 leading-none">{num}</div>
                      <div className="text-[10px] text-gray-400">{mon}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {visibleRooms.map(room => (
                <tr key={room.roomId} className="border-b border-gray-50">
                  <td className="sticky left-0 z-10 bg-white px-4 py-3 align-middle">
                    <div className="flex items-start gap-2">
                      <BedDouble size={15} className="text-gray-300 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 leading-tight truncate">{room.name}</p>
                        <p className="text-[11px] text-gray-400">
                          {room.totalUnits} room{room.totalUnits !== 1 ? "s" : ""}
                          {tab === "Rates" && <> · base {fmtMoney(room.basePrice)}</>}
                        </p>
                        {tab === "Rates" && (
                          <button onClick={() => setBulkFor(room)}
                            className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-amber-600 hover:text-amber-700">
                            <Wand2 size={11} /> Set rate
                          </button>
                        )}
                      </div>
                    </div>
                  </td>

                  {room.cells.map(c => {
                    const { weekend } = dayMeta(c.date);
                    if (tab === "Inventory") {
                      const out = c.available === 0;
                      return (
                        <td key={c.date} className={`px-1.5 py-3 text-center border-l border-gray-50
                          ${weekend ? "bg-amber-50/40" : ""}`}>
                          <span className={`text-base font-bold tabular-nums ${out ? "text-rose-600" : "text-gray-800"}`}>
                            {c.available}
                          </span>
                          <div className="text-[10px] text-gray-400 tabular-nums">of {room.totalUnits}</div>
                        </td>
                      );
                    }
                    // A rate differing from base means an override is in force.
                    const overridden = c.rate !== room.basePrice;
                    return (
                      <td key={c.date} className={`px-1.5 py-2 text-center border-l border-gray-50
                        ${weekend ? "bg-amber-50/40" : ""}`}>
                        <div className={`rounded-lg border px-1 py-1.5 text-[12.5px] font-semibold tabular-nums
                          ${overridden
                            ? "border-amber-300 bg-amber-50 text-amber-700"
                            : "border-gray-200 bg-white text-gray-800"}`}>
                          {fmtMoney(c.rate)}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>

            {/* The three-row footer the channel-manager grids use — it answers
                "can I still sell tonight" at a glance. Scoped to the filter. */}
            <tfoot className="bg-gray-50/70">
              {[
                { key: "sold",      label: "Sold Rooms" },
                { key: "available", label: "Available Inventory" },
                { key: "total",     label: "Total Rooms" },
              ].map(({ key, label }) => (
                <tr key={key} className="border-t border-gray-100">
                  <td className="sticky left-0 z-10 bg-gray-50/70 px-4 py-2 text-[11px] font-semibold
                    text-gray-500 uppercase tracking-wider whitespace-nowrap">{label}</td>
                  {totals.map(t => (
                    <td key={t.date} className="px-1.5 py-2 text-center border-l border-gray-100">
                      <span className={`text-xs font-semibold tabular-nums
                        ${key === "available" && t.available === 0 ? "text-rose-600" : "text-gray-700"}`}>
                        {t[key]}
                      </span>
                    </td>
                  ))}
                </tr>
              ))}
            </tfoot>
          </table>
        </div>
      )}

      {data && (
        <p className="text-[11px] text-gray-400">
          Shaded columns are weekends.
          {tab === "Rates" && " Amber cells are seasonal overrides."}
          {tab === "Inventory" && " Red means nothing left to sell."}
        </p>
      )}

      {bulkFor && (
        <BulkRateModal room={bulkFor} authHeaders={authHeaders}
          onClose={() => setBulkFor(null)}
          // Removing a rate refreshes the grid but keeps the panel open, so
          // several can be cleared in one go; applying a new one closes it.
          onSaved={(opts) => { if (!opts?.keepOpen) setBulkFor(null); load(); }} />
      )}
    </div>
  );
}

/* ── OTA prices ──────────────────────────────────────────────────────────
   This property's own price on each OTA, as an adjustment to its HostSync
   price: "Booking.com +15%". The website keeps the HostSync price; every
   date, season and guest-count price follows automatically. A price typed
   into an OTA's own extranet is overwritten by the channel manager, which
   is why it lives here. Shown only once the property is on the channel
   manager. */
function OtaPricingPanel({ rooms, authHeaders }) {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const [rows, setRows] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetch(`${API}/ota-pricing`, { headers: authHeaders() })
      .then(r => r.json())
      .then(j => {
        if (!j.success) return;
        setInfo(j.data);
        setRows(Object.fromEntries(j.data.channels.map(c => {
          const a = j.data.adjustments.find(x => x.channel === c.key);
          return [c.key, { mode: a?.mode || "percent", value: a ? String(a.value) : "" }];
        })));
      })
      .catch(() => {});
  }, []);

  if (!info?.connected) return null;

  const sample = rooms.find(r => r.basePrice > 0);
  const preview = (row) => {
    const v = Number(row.value);
    if (!sample || !row.value || !Number.isFinite(v) || v === 0) return null;
    const out = Math.max(0, Math.round(row.mode === "amount" ? sample.basePrice + v : sample.basePrice * (1 + v / 100)));
    return `₹${sample.basePrice.toLocaleString("en-IN")} → ₹${out.toLocaleString("en-IN")}`;
  };

  async function save() {
    setSaving(true); setError(""); setSaved(false);
    try {
      const adjustments = info.channels
        .map(c => ({ channel: c.key, mode: rows[c.key]?.mode || "percent", value: rows[c.key]?.value }))
        .filter(a => a.value !== "" && a.value !== undefined);
      const res = await fetch(`${API}/ota-pricing`, {
        method: "PUT", headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ adjustments }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Could not save");
      setInfo(i => ({ ...i, adjustments: json.data.adjustments }));
      setSaved(true);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  const fieldCls = "border border-gray-200 rounded-xl px-3 min-h-[44px] text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white";

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 min-h-[44px] text-left">
        <span className="flex items-center gap-2">
          <Globe size={17} className="text-amber-500" />
          <span className="font-semibold text-gray-900">OTA prices</span>
          <span className="text-xs text-gray-400 hidden sm:inline">
            your own price on Booking.com, Airbnb, MakeMyTrip…
          </span>
        </span>
        <ChevronRight size={18} className={`text-gray-400 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-4 border-t border-gray-100 pt-4">
          <p className="text-xs text-gray-500 leading-relaxed">
            Your website keeps your HostSync price. Set how much each OTA charges on top of it (or below, with a minus).
            It follows every date, seasonal rate and guest count automatically. Prices changed on an OTA's own website
            are overwritten — set them here instead.
          </p>

          <div className="space-y-2">
            {info.channels.map(c => {
              const row = rows[c.key] || { mode: "percent", value: "" };
              const savedAdj = info.adjustments.find(a => a.channel === c.key);
              return (
                <div key={c.key} className="flex flex-wrap items-center gap-2 sm:gap-3 rounded-xl border border-gray-100 px-3 py-2">
                  <span className="w-full sm:w-44 text-sm font-medium text-gray-800">{c.label}</span>
                  <select value={row.mode} aria-label={`${c.label} adjustment type`}
                    onChange={e => setRows(r => ({ ...r, [c.key]: { ...row, mode: e.target.value } }))}
                    className={fieldCls}>
                    <option value="percent">%</option>
                    <option value="amount">₹ per night</option>
                  </select>
                  <input type="number" inputMode="decimal" value={row.value} placeholder="0" aria-label={`${c.label} adjustment`}
                    onChange={e => setRows(r => ({ ...r, [c.key]: { ...row, value: e.target.value } }))}
                    className={`${fieldCls} w-24`} />
                  <span className="text-xs text-gray-400 tabular-nums">{preview(row) || "same as HostSync"}</span>
                  {savedAdj && (
                    <span className={`ml-auto text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                      info.active[c.key] ? "text-emerald-700 bg-emerald-50" : "text-amber-700 bg-amber-50"}`}>
                      {info.active[c.key] ? "Sending" : "Being set up by HostSync"}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {sample && <p className="text-[11px] text-gray-400">Examples use {sample.name}'s base price.</p>}
          {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>}
          {saved && (
            <p className="text-sm text-emerald-700 bg-emerald-50 rounded-xl px-3 py-2">
              Saved. OTAs marked "Sending" update within a minute; HostSync connects the others for you.
            </p>
          )}
          <button onClick={save} disabled={saving}
            className="min-h-[44px] px-5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-semibold disabled:opacity-60">
            {saving ? "Saving…" : "Save OTA prices"}
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Set rates & rules ──────────────────────────────────────────────────
   Price, minimum/maximum nights, stop sell and closed-to-arrival/departure,
   across any set of rooms and a date range.

   Lines are collected and sent together on Apply. That is not cosmetic: one
   request is one save is ONE message to the channel manager, which is what
   the OTAs (and Channex certification) expect from a single user action.
   Applying four lines one at a time would send four.                       */
function BulkRulesPanel({ rooms, authHeaders, onApplied }) {
  const blank = () => ({
    roomIds: [], startDate: toLocalISODate(new Date()), endDate: toLocalISODate(new Date()),
    price: "", minStay: "", maxStay: "", stopSell: false, closedToArrival: false, closedToDeparture: false,
  });
  const [open, setOpen]   = useState(false);
  const [lines, setLines] = useState([blank()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone]   = useState("");

  const update = (i, patch) => setLines(ls => ls.map((l, n) => (n === i ? { ...l, ...patch } : l)));
  const toggleRoom = (i, id) => update(i, {
    roomIds: lines[i].roomIds.includes(id) ? lines[i].roomIds.filter(r => r !== id) : [...lines[i].roomIds, id],
  });

  async function apply() {
    setError(""); setDone("");
    // Send only what each line actually sets, so a line about stop sell
    // never silently rewrites a price.
    const changes = lines.map(l => ({
      roomIds: l.roomIds, startDate: l.startDate, endDate: l.endDate,
      ...(l.price !== "" && { price: Number(l.price) }),
      ...(l.minStay !== "" && { minStay: Number(l.minStay) }),
      ...(l.maxStay !== "" && { maxStay: Number(l.maxStay) }),
      ...(l.stopSell && { stopSell: true }),
      ...(l.closedToArrival && { closedToArrival: true }),
      ...(l.closedToDeparture && { closedToDeparture: true }),
    }));
    if (changes.some(c => !c.roomIds.length)) { setError("Pick at least one room on every line."); return; }

    setSaving(true);
    try {
      const res = await fetch(`${API}/rates/bulk`, {
        // authHeaders is a function here, as everywhere else on this page.
        method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ changes }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Could not apply those changes.");
      setDone(`Applied ${changes.length} change${changes.length !== 1 ? "s" : ""}. Your connected OTAs are being updated.`);
      setLines([blank()]);
      onApplied?.();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  if (!rooms.length) return null;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 min-h-[44px] text-left">
        <span className="flex items-center gap-2">
          <SlidersHorizontal size={17} className="text-amber-500" />
          <span className="font-semibold text-gray-900">Set rates &amp; rules</span>
          <span className="text-xs text-gray-400 hidden sm:inline">
            price, minimum nights, stop sell — across rooms and dates
          </span>
        </span>
        <ChevronRight size={18} className={`text-gray-400 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-4 border-t border-gray-100 pt-4">
          {lines.map((line, i) => (
            <div key={i} className="rounded-xl border border-gray-200 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Change {i + 1}</span>
                {lines.length > 1 && (
                  <button onClick={() => setLines(ls => ls.filter((_, n) => n !== i))}
                    className="tap-44 p-1.5 rounded-lg hover:bg-gray-100" aria-label="Remove this change">
                    <X size={15} className="text-gray-400" />
                  </button>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                {rooms.map(r => (
                  <button key={r.roomId} onClick={() => toggleRoom(i, r.roomId)}
                    className={`px-3 py-2 min-h-[44px] rounded-xl text-sm border transition-colors ${
                      line.roomIds.includes(r.roomId)
                        ? "bg-amber-50 border-amber-300 text-amber-800 font-medium"
                        : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>
                    {r.name}
                  </button>
                ))}
                <button onClick={() => update(i, { roomIds: line.roomIds.length === rooms.length ? [] : rooms.map(r => r.roomId) })}
                  className="px-3 py-2 min-h-[44px] rounded-xl text-sm text-amber-600 font-medium">
                  {line.roomIds.length === rooms.length ? "Clear" : "All rooms"}
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">From</span>
                  <input type="date" value={line.startDate} onChange={e => update(i, { startDate: e.target.value })}
                    className={inputCls} />
                </label>
                <label className="block">
                  <span className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">To</span>
                  <input type="date" value={line.endDate} onChange={e => update(i, { endDate: e.target.value })}
                    className={inputCls} />
                </label>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <label className="block">
                  <span className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Rate</span>
                  <input type="number" inputMode="decimal" placeholder="—" value={line.price}
                    onChange={e => update(i, { price: e.target.value })} className={inputCls} />
                </label>
                <label className="block">
                  <span className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Min nights</span>
                  <input type="number" inputMode="numeric" placeholder="—" value={line.minStay}
                    onChange={e => update(i, { minStay: e.target.value })} className={inputCls} />
                </label>
                <label className="block">
                  <span className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Max nights</span>
                  <input type="number" inputMode="numeric" placeholder="—" value={line.maxStay}
                    onChange={e => update(i, { maxStay: e.target.value })} className={inputCls} />
                </label>
              </div>

              <div className="flex flex-wrap gap-2">
                {[["stopSell", "Stop sell"], ["closedToArrival", "No check-in"], ["closedToDeparture", "No check-out"]].map(([key, label]) => (
                  <button key={key} onClick={() => update(i, { [key]: !line[key] })}
                    className={`px-3 py-2 min-h-[44px] rounded-xl text-sm border transition-colors ${
                      line[key] ? "bg-red-50 border-red-300 text-red-700 font-medium"
                                : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>
                    {label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-gray-400">
                Leave a box empty to leave it as it is. Stop sell closes these dates on your own site and every
                connected OTA.
              </p>
            </div>
          ))}

          <div className="flex flex-wrap items-center gap-3">
            <button onClick={() => setLines(ls => [...ls, blank()])}
              className="flex items-center gap-1.5 px-4 py-2.5 min-h-[44px] rounded-xl border border-gray-200
                text-sm font-medium text-gray-700 hover:border-gray-300">
              <Plus size={15} /> Add another change
            </button>
            <button onClick={apply} disabled={saving}
              className="px-5 py-2.5 min-h-[44px] rounded-xl bg-amber-500 text-white text-sm font-semibold
                hover:bg-amber-600 disabled:opacity-60 transition-colors">
              {saving ? "Applying…" : `Apply ${lines.length > 1 ? `${lines.length} changes` : "change"}`}
            </button>
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-3">{error}</p>}
          {done  && <p className="text-sm text-emerald-700 bg-emerald-50 rounded-xl px-4 py-3">{done}</p>}
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full border border-gray-200 rounded-xl px-3 py-2.5 min-h-[44px] text-sm " +
  "focus:outline-none focus:ring-2 focus:ring-amber-400";

/* Posts to the same validated bulk endpoint as Rooms → Seasonal Pricing, so
   there is one way to set a rate and this is a shortcut to it. */
function BulkRateModal({ room, authHeaders, onClose, onSaved }) {
  // Existing seasons for this room, so a rate can be REMOVED from here and not
  // only from Rooms -> Seasonal Pricing. Setting a rate and cancelling one are
  // the same job; making someone leave this screen to undo what they just did
  // here is why "how do I cancel it" needed asking.
  const [seasons, setSeasons] = useState(null);
  const [removingId, setRemovingId] = useState("");

  const loadSeasons = async () => {
    try {
      const res  = await fetch(`${API}/rooms`, { headers: authHeaders() });
      const json = await res.json();
      const mine = (json.data || []).find(r => String(r._id) === String(room.roomId));
      setSeasons(mine?.rateOverrides || []);
    } catch { setSeasons([]); }
  };
  useEffect(() => { loadSeasons(); }, []); // eslint-disable-line

  async function removeSeason(seasonId) {
    if (!window.confirm("Remove this rate? Those nights go back to the room's base rate.")) return;
    setRemovingId(seasonId);
    try {
      const res  = await fetch(`${API}/rooms/${room.roomId}/rate-overrides/${seasonId}`, {
        method: "DELETE", headers: authHeaders(),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      await loadSeasons();
      onSaved?.({ keepOpen: true });   // refresh the grid behind the modal
    } catch (e) { setError(e.message); }
    finally { setRemovingId(""); }
  }

  const [startDate, setStartDate] = useState(toLocalISODate(new Date()));
  const [endDate, setEndDate]     = useState("");
  const [price, setPrice]         = useState("");
  const [label, setLabel]         = useState("");
  const [weekdays, setWeekdays]   = useState([]);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState("");

  const weekendsOn = weekdays.length === 2 && weekdays.includes(0) && weekdays.includes(6);

  async function save() {
    setError("");
    if (!startDate || !endDate || !price) { setError("Start date, end date and price are required."); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/rooms/${room.roomId}/rate-overrides/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ startDate, endDate, price: parseFloat(price), label, weekdays }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      onSaved();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div>
          <h2 className="text-lg font-bold text-gray-900">Set rate — {room.name}</h2>
          <p className="text-xs text-gray-500 mt-0.5">Base is {fmtMoney(room.basePrice)} a night.</p>
        </div>

        {seasons !== null && seasons.length > 0 && (
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Rates set for this room</label>
            <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 max-h-40 overflow-y-auto">
              {seasons.map(sn => {
                const ranges = sn.dateRanges || [];
                const first = ranges[0];
                const span = first
                  ? `${new Date(first.startDate).toLocaleDateString("en-IN",{day:"numeric",month:"short"})} – ${new Date(first.endDate).toLocaleDateString("en-IN",{day:"numeric",month:"short"})}`
                  : "—";
                return (
                  <div key={sn._id} className="flex items-center gap-2 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {fmtMoney(sn.price)}{sn.label ? ` · ${sn.label}` : ""}
                      </p>
                      <p className="text-[11px] text-gray-400">
                        {span}{ranges.length > 1 ? ` +${ranges.length - 1} more range${ranges.length > 2 ? "s" : ""}` : ""}
                      </p>
                    </div>
                    <button type="button" onClick={() => removeSeason(sn._id)} disabled={removingId === sn._id}
                      title="Remove this rate"
                      className="tap-44 px-2 py-1 rounded-lg text-[11px] font-medium text-rose-600
                        hover:bg-rose-50 disabled:opacity-50 flex-shrink-0">
                      {removingId === sn._id ? "Removing…" : "Remove"}
                    </button>
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-gray-400 mt-1">
              Removing one puts those nights back on the base rate.
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">From</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">To</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Rate per night</label>
            <input type="number" min="1" value={price} onChange={e => setPrice(e.target.value)}
              placeholder={String(room.basePrice || "")}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Label (optional)</label>
            <input value={label} onChange={e => setLabel(e.target.value)} placeholder="Diwali, Peak…"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs text-gray-500">Apply to</label>
            <button type="button" onClick={() => setWeekdays(weekendsOn ? [] : [0, 6])}
              className="text-[11px] font-medium text-amber-600 hover:text-amber-700">
              {weekendsOn ? "Clear" : "Weekends only"}
            </button>
          </div>
          <div className="flex gap-1">
            {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => {
              const on = weekdays.includes(i);
              return (
                <button key={i} type="button" aria-pressed={on}
                  onClick={() => setWeekdays(w => (w.includes(i) ? w.filter(x => x !== i) : [...w, i].sort()))}
                  className={`flex-1 min-h-[38px] rounded-lg text-xs font-semibold border transition-colors
                    ${on ? "bg-amber-500 border-amber-500 text-white"
                         : "bg-gray-50 border-gray-200 text-gray-500 hover:border-amber-200"}`}>
                  {d}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-gray-400 mt-1">
            {weekdays.length === 0 ? "Every day in the range." : "Only the selected days."}
          </p>
        </div>

        {error && <p className="text-xs text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>}

        <div className="flex gap-2">
          <button onClick={onClose}
            className="flex-1 min-h-[44px] rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={save} disabled={saving}
            className="flex-1 min-h-[44px] rounded-xl bg-gradient-to-r from-amber-500 to-orange-500
              text-white text-sm font-semibold disabled:opacity-60">
            {saving ? "Saving…" : "Apply rate"}
          </button>
        </div>
      </div>
    </div>
  );
}
